import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import instance from "./axiosInstance";
import {
  CreateFiveYearsInspectionDTO,
  FiveYearsInspection,
} from "types/fiveYearsInspection/fiveYearsInspection.type";
import { useNotification } from "hooks/useNotification";

const ENDPOINT = "/five-years-inspection";
export enum FiveYearsInspectionQueryKey {
  GetOne = "GET_FIVE_YEARS_INSPECTION-",
}

export const useGetFiveYearsInspection = (id: number, shouldPoll = false) =>
  useQuery({
    queryKey: [FiveYearsInspectionQueryKey.GetOne, id],
    queryFn: async (): Promise<FiveYearsInspection> => {
      const response = await instance.get(`${ENDPOINT}/${id}`);
      return response.data;
    },
    refetchInterval: shouldPoll ? 3000 : false,
  });

export const useCreateFiveYearsInspection = () => {
  const notification = useNotification();

  return useMutation({
    mutationFn: async (
      payload: CreateFiveYearsInspectionDTO,
    ): Promise<FiveYearsInspection> => {
      const response = await instance.post(ENDPOINT, payload);
      return response.data;
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
  });
};

export const useUpdateFiveYearsInspection = (options = {}) => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (payload: any): Promise<FiveYearsInspection> => {
      const { emails, separator, ...rest } = payload;
      const response = await instance.put(ENDPOINT, rest);
      return response.data;
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
    onSuccess: (result: FiveYearsInspection) => {
      notification.info("Ändringarna sparade");
      queryClient.invalidateQueries({
        queryKey: [FiveYearsInspectionQueryKey.GetOne, result.id],
      });
    },
    ...options,
  });
};

export const useSetFiveYearsInspectionFiles = (options = {}) => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (
      payload: InspectionFilesDTO,
    ): Promise<FiveYearsInspection> => {
      const response = await instance.put(ENDPOINT + "/files", payload);
      return response.data;
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
    onSuccess: (result: FiveYearsInspection) => {
      queryClient.invalidateQueries({
        queryKey: [FiveYearsInspectionQueryKey.GetOne, result.id],
      });
    },
    ...options,
  });
};

export const useSendFiveYearsInspectionAttachments = () => {
  const notification = useNotification();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      id: number;
      includeTransportDocument: boolean;
    }): Promise<number> => {
      const response = await instance.post(
        ENDPOINT + "/send-attachments",
        payload,
      );
      return response.data;
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
    onSuccess: (inspectionId: number) => {
      notification.info("Utskick schemalagt");
      queryClient.invalidateQueries({
        queryKey: [FiveYearsInspectionQueryKey.GetOne, inspectionId],
      });
    },
  });
};

export type InspectionFilesDTO = {
  inspectionId: number;
  files: FileDTO[];
};

export type FileDTO = {
  id?: number;
  name: string;
  url: string;
  nameWithoutExtension?: string;
  extension?: string;
};

export const deleteFiveYearsInspection = () => ({
  mutationFn: async (id: number): Promise<void> =>
    (await instance.delete(`${ENDPOINT}/${id}`)).data,
});
